"use client";

import { useCallback } from "react";
import { toast } from "sonner";

import { useCommandSession } from "@/components/aios-command";
import { useCreateCommandScheduleJob } from "@/hooks/use-command-scheduler-job";
import { useAppSelector } from "@/redux/hooks";
import {
    buildCronExpression,
    computeNextDailyRun,
    computeNextRecurringRun,
    localHmToUtcHm,
    parseHm,
} from "@/utils/command-scheduler";

interface CommandV2ScheduleDialogInput {
    cadenceLabel: string;
    time: string;
    description: string;
    prompt: string;
}

interface CommandV2SchedulerSubmitParams {
    prompt: string;
    agentId?: string | null;
    sessionId?: string | null;
    fileIds?: string[];
    runAt: Date;
    description: string;
    scheduleType: "one_time" | "recurring";
    cronExpression: string | null;
    newSession: boolean;
}

function createRandomSessionId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useCommandV2CreateSchedule() {
    const { agentId } = useCommandSession();
    const user = useAppSelector((state) => state.user.user);
    const scheduleMutation = useCreateCommandScheduleJob();

    const runScheduleMutation = useCallback(
        (
            input: {
                agentId: string;
                prompt: string;
                description: string;
                runAt: Date;
                sessionId: string;
                newSession: boolean;
                fileIds: string[];
                scheduleType: "one_time" | "recurring";
                cronExpression: string | null;
            },
            onSuccess?: () => void
        ) => {
            if (!input.agentId) {
                toast.error("Select an agent");
                return false;
            }

            const userId = user?.id ?? "";
            const organizationId = user?.organization?.id ?? "";
            const commandTeamId = user?.organization?.command_team?.id ?? "";
            if (!userId || !organizationId || !commandTeamId) {
                toast.error("Account is not loaded yet. Try again in a moment.");
                return false;
            }

            scheduleMutation.mutate(
                {
                    sessionId: input.sessionId,
                    newSession: input.newSession,
                    agentId: input.agentId,
                    prompt: input.prompt,
                    description: input.description,
                    runAt: input.runAt,
                    fileIds: input.fileIds,
                    userId,
                    organizationId,
                    commandTeamId,
                    analystTeamId: user?.organization?.analyst_team?.id?.trim() || null,
                    salesTeamId: user?.organization?.sales_team?.id?.trim() || null,
                    supportTeamId: user?.organization?.support_team?.id?.trim() || null,
                    tasksTeamId: user?.organization?.tasks_team?.id?.trim() || null,
                    computerUseTeamId: user?.organization?.computer_use_team?.id?.trim() || null,
                    scheduleType: input.scheduleType,
                    cronExpression: input.cronExpression,
                },
                { onSuccess: () => onSuccess?.() }
            );
            return true;
        },
        [scheduleMutation, user]
    );

    const createFromSchedulerSubmit = useCallback(
        (params: CommandV2SchedulerSubmitParams, onSuccess?: () => void) => {
            if (!scheduleMutation.hasPermission) {
                toast.error("You don't have permission to schedule messages");
                return false;
            }

            const resolvedAgentId = params.agentId ?? agentId;
            if (!resolvedAgentId) {
                toast.error("Select an agent", {
                    description: "Choose which Command agent should run this schedule.",
                });
                return false;
            }

            const prompt = params.prompt.trim();
            if (!prompt) {
                toast.error("Add a message to schedule");
                return false;
            }

            const isNewSession = params.newSession ?? !params.sessionId;
            const resolvedSessionId = params.sessionId ?? createRandomSessionId();

            return runScheduleMutation(
                {
                    agentId: resolvedAgentId,
                    prompt,
                    description: params.description.trim(),
                    runAt: params.runAt,
                    sessionId: resolvedSessionId,
                    newSession: isNewSession,
                    fileIds: params.fileIds ?? [],
                    scheduleType: params.scheduleType,
                    cronExpression: params.cronExpression,
                },
                onSuccess
            );
        },
        [agentId, runScheduleMutation, scheduleMutation.hasPermission]
    );

    const createFromDialog = useCallback(
        (input: CommandV2ScheduleDialogInput, targetAgentId?: string | null) => {
            const prompt = input.prompt.trim();
            if (!prompt) return false;

            const resolvedAgentId = targetAgentId ?? agentId;
            if (!resolvedAgentId) {
                toast.error("Select an agent");
                return false;
            }

            const isOnce = input.cadenceLabel === "once";
            const frequency =
                input.cadenceLabel === "weekly" || input.cadenceLabel === "monthly"
                    ? input.cadenceLabel
                    : "daily";
            const { h, m } = parseHm(input.time);
            const runAt = isOnce
                ? computeNextDailyRun(h, m)
                : computeNextRecurringRun(frequency, input.time, [1], 1);

            return runScheduleMutation({
                agentId: resolvedAgentId,
                prompt,
                description: input.description.trim(),
                runAt,
                sessionId: createRandomSessionId(),
                newSession: true,
                fileIds: [],
                scheduleType: isOnce ? "one_time" : "recurring",
                cronExpression: isOnce
                    ? null
                    : buildCronExpression(frequency, localHmToUtcHm(input.time), [1], 1),
            });
        },
        [agentId, runScheduleMutation]
    );

    return {
        agentId,
        createFromDialog,
        createFromSchedulerSubmit,
        isPending: scheduleMutation.isPending,
        hasPermission: scheduleMutation.hasPermission,
    };
}
